import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiFile, FiDownload, FiEdit2, FiEye, FiX, FiRefreshCw } from 'react-icons/fi';
import api, { getDownloadBaseUrl } from '../services/api';
import TableFilters from '../components/TableFilters';
import DocumentoGisPreview from '../components/DocumentoGisPreview';
import DocumentoWordPreview from '../components/DocumentoWordPreview';
import { useAuth } from '../context/AuthContext';
import './Documentos.css';


const formatDate = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '-');
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg']);
const WORD_EXTENSIONS = new Set(['doc', 'docx']);
const GIS_EXTENSIONS = new Set(['shp', 'shx', 'dbf', 'prj', 'cpg', 'qpj', 'sbn', 'sbx', 'kml', 'kmz', 'zip', 'geojson', 'gpx']);
const TIPOS_DOCUMENTO = ['Licença', 'Laudo', 'Estudo', 'Relatório', 'ART', 'Contrato', 'Plano', 'Manifesto', 'Certidão', 'Nota Fiscal', 'Comprovante', 'Imagem', 'Logotipo', 'Fotografia', 'Shapefile', 'TAC (Termo de Ajuste de Conduta)', 'Conversão de Multa', 'Alvará', 'Projeto', 'Planilha', 'Outro'];

const getArquivoUrl = (arquivo) => `${getDownloadBaseUrl()}${arquivo}`;
const getArquivoExtensao = (arquivo) => String(arquivo || '').split('.').pop().toLowerCase();

const DocumentoPreview = ({ arquivo, titulo, documentoId, textoExtraido }) => {
  const url = getArquivoUrl(arquivo);
  const extensao = getArquivoExtensao(arquivo);

  if (IMAGE_EXTENSIONS.has(extensao)) {
    return <img src={url} alt={titulo || 'Documento'} className="modal-documento-preview-image" />;
  }

  if (extensao === 'pdf') {
    return <iframe src={url} title={titulo || 'Documento PDF'} className="modal-documento-preview-frame" />;
  }

  if (WORD_EXTENSIONS.has(extensao)) {
    return (
      <DocumentoWordPreview
        arquivo={arquivo}
        titulo={titulo}
        url={url}
        documentoId={documentoId}
        textoExtraido={textoExtraido}
      />
    );
  }

  if (GIS_EXTENSIONS.has(extensao)) {
    return <DocumentoGisPreview arquivo={arquivo} titulo={titulo} url={url} />;
  }

  return (
    <p className="modal-documento-preview-empty">
      Este formato não possui visualização incorporada no navegador. Use o botão de download para abrir o arquivo.
    </p>
  );
};

const Documentos = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [documentos, setDocumentos] = useState([]);
  const [total, setTotal] = useState(0);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [licencas, setLicencas] = useState([]);
  const [condicionantes, setCondicionantes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [tarefas, setTarefas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEmp, setFiltroEmp] = useState('');
  const [filtroLicenca, setFiltroLicenca] = useState('');
  const [filtroCond, setFiltroCond] = useState('');
  const [filtroResponsavel, setFiltroResponsavel] = useState('');
  const [filtroVencimentoDe, setFiltroVencimentoDe] = useState('');
  const [filtroVencimentoAte, setFiltroVencimentoAte] = useState('');
  const [search, setSearch] = useState('');
  const sort = 'criado_em';
  const order = 'desc';
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [docSelecionado, setDocSelecionado] = useState(null);
  const [reprocessingId, setReprocessingId] = useState(null);
  const [reprocessingAll, setReprocessingAll] = useState(false);
  const [editando, setEditando] = useState(null);
  const [editForm, setEditForm] = useState({ tipo: '', descricao: '', empreendimento_id: '', licenca_id: '', tarefa_id: '', data_documento: '' });

  const carregarAuxiliares = useCallback(async () => {
    try {
      const [empRes, licRes, tarefasRes] = await Promise.all([
        api.get('/api/empreendimentos'),
        api.get('/api/licencas').catch(() => ({ data: [] })),
        api.get('/api/tarefas').catch(() => ({ data: [] }))
      ]);
      const empList = Array.isArray(empRes.data) ? empRes.data : (empRes.data?.data || []);
      setEmpreendimentos(empList);
      setLicencas(Array.isArray(licRes.data) ? licRes.data : (licRes.data?.data || licRes.data || []));
      setTarefas(Array.isArray(tarefasRes.data) ? tarefasRes.data : (tarefasRes.data?.data || []));
      const usuariosPromises = empList.map((emp) =>
        api.get(`/api/usuarios/empreendimento/${emp.id}`).catch(() => ({ data: [] }))
      );
      const usuariosRes = await Promise.all(usuariosPromises);
      const usuariosList = usuariosRes.flatMap((r) => r.data || []);
      setUsuarios([...new Map(usuariosList.map((u) => [u.id, u])).values()]);
      try {
        const condRes = await api.get('/api/condicionantes');
        setCondicionantes(condRes.data || []);
      } catch {
        const licencasList = Array.isArray(licRes.data) ? licRes.data : (licRes.data?.data || licRes.data || []);
        const condPromises = licencasList.map((lic) =>
          api.get(`/api/condicionantes?licenca_id=${lic.id}`).catch(() => ({ data: [] }))
        );
        const condRes = await Promise.all(condPromises);
        setCondicionantes(condRes.flatMap((r) => r.data || []));
      }
    } catch (err) {
      console.error('Erro ao carregar auxiliares:', err);
    }
  }, []);

  const carregarDocumentos = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtroEmp) params.set('empreendimento_id', filtroEmp);
      if (filtroLicenca) params.set('licenca_id', filtroLicenca);
      if (filtroCond) params.set('condicionante_id', filtroCond);
      if (filtroResponsavel) params.set('responsavel_id', filtroResponsavel);
      if (filtroVencimentoDe) params.set('vencimento_de', filtroVencimentoDe);
      if (filtroVencimentoAte) params.set('vencimento_ate', filtroVencimentoAte);
      if (search) params.set('q', search);
      params.set('sort', sort);
      params.set('order', order);
      params.set('page', page);
      params.set('limit', limit);
      const res = await api.get(`/api/documentos?${params}`);
      const data = res.data;
      if (data && typeof data === 'object' && 'data' in data) {
        setDocumentos(data.data || []);
        setTotal(data.total || 0);
      } else {
        setDocumentos(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (err) {
      console.error('Erro ao carregar documentos:', err);
      setDocumentos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filtroEmp, filtroLicenca, filtroCond, filtroResponsavel, filtroVencimentoDe, filtroVencimentoAte, search, sort, order, page, limit]);

  useEffect(() => {
    carregarAuxiliares();
  }, [carregarAuxiliares]);

  useEffect(() => {
    carregarDocumentos();
  }, [carregarDocumentos]);

  const handleReprocess = async (doc) => {
    try {
      setReprocessingId(doc.id);
      const res = await api.post(`/api/documentos/${doc.id}/reprocessar`);
      const data = res.data || {};
      if (data.resultado === 'licenca_criada' && data.licenca) {
        await carregarDocumentos();
        const abrirValidacao = window.confirm('O documento foi identificado como licença e enviado para extração. Deseja abrir a tela de validação agora?');
        if (abrirValidacao && data.licenca.redirect) navigate(data.licenca.redirect);
        return;
      }
      await carregarDocumentos();
      alert('Documento reprocessado com sucesso.');
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.error;
      alert(message || (status === 404
        ? 'O recurso de reprocessamento ainda não está disponível no servidor. Atualize ou reinicie o backend e tente novamente.'
        : 'Não foi possível reprocessar o documento.'));
    } finally {
      setReprocessingId(null);
    }
  };

  const handleReprocessAll = async () => {
    if (!window.confirm('Reprocessar todos os documentos sem resumo? A análise será executada novamente para cada documento que falhou anteriormente.')) return;
    try {
      setReprocessingAll(true);
      const res = await api.post('/api/documentos/reprocessar-todos', { background: true });
      const data = res.data || {};

      if (res.status === 202) {
        alert([
          `Reprocessamento iniciado em segundo plano (${data.total || 0} documento(s)).`,
          `Pipeline: extração/OCR (${data.extracao_concorrencia || 1} em paralelo) → IA (${data.ia_concorrencia || 2} em paralelo).`,
          '',
          'A extração não espera a IA — as filas rodam em paralelo.',
          '',
          'Acompanhe: pm2 logs greencheck | grep -E "extracao|reprocessar-documento"',
          'Status: GET /api/documentos/reprocessar-todos/status'
        ].join('\n'));
        return;
      }

      await carregarDocumentos();
      const linhas = [
        `Documentos sem resumo encontrados: ${data.total || 0}.`,
        `Reprocessados com sucesso: ${data.reprocessados || 0}.`,
        `Licenças identificadas e encaminhadas para extração: ${data.licencas_criadas || 0}.`,
        `Falhas: ${data.falhas || 0}.`
      ];
      if (data.duracao_total_ms) {
        linhas.push(`Tempo total: ${Math.round(data.duracao_total_ms / 1000)}s.`);
      }
      if (data.interrompido) linhas.push('O processamento foi interrompido porque a IA atingiu o limite de uso. Os documentos restantes poderão ser reprocessados mais tarde.');
      const falhas = (data.itens || []).filter((item) => item.status === 'falha');
      if (falhas.length > 0) {
        linhas.push('', 'Documentos com falha:');
        falhas.slice(0, 15).forEach((item) => {
          linhas.push(`- ${item.titulo || item.documento_id}: ${item.erro || 'erro desconhecido'}`);
        });
        if (falhas.length > 15) linhas.push(`... e mais ${falhas.length - 15} falha(s). Veja pm2 logs greencheck para o detalhe completo.`);
      }
      alert(linhas.join('\n'));
    } catch (err) {
      alert(err.response?.data?.error || 'Não foi possível reprocessar os documentos em lote.');
    } finally {
      setReprocessingAll(false);
    }
  };

  const abrirEdicao = (doc) => {
    setEditando(doc);
    setEditForm({
      tipo: doc.tipo || 'Outro',
      descricao: doc.descricao || '',
      empreendimento_id: doc.empreendimento_id_resolvido || doc.empreendimento_id || '',
      licenca_id: doc.licenca_id_resolvido || doc.licenca_id || '',
      tarefa_id: doc.tarefa_id || '',
      data_documento: doc.data_documento ? String(doc.data_documento).slice(0, 10) : ''
    });
  };

  const salvarEdicao = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/documentos/${editando.id}`, editForm);
      setEditando(null);
      await carregarDocumentos();
    } catch (err) {
      alert(err.response?.data?.error || 'Não foi possível atualizar os dados do documento.');
    }
  };

  if (loading && documentos.length === 0) {
    return (
      <div className="documentos-page">
        <div className="loading">Carregando documentos...</div>
      </div>
    );
  }

  return (
    <div className="documentos-page">
      <header className="page-header">
        <h1 className="page-title">Documentos</h1>
        {['admin_sistema', 'admin_empreendimento'].includes(user?.role) && (
          <button type="button" className="btn btn-primary" onClick={handleReprocessAll} disabled={reprocessingAll}>
            <FiRefreshCw /> {reprocessingAll ? 'Reprocessando...' : 'Reprocessar todos'}
          </button>
        )}
      </header>

      <TableFilters
        search={search}
        onSearchChange={setSearch}
        filterRows={[
          ['search', 'empreendimento', 'licenca', 'condicionante', 'responsavel'],
          ['vencimento_de', 'vencimento_ate']
        ]}
        filters={[
          {
            key: 'empreendimento',
            label: 'Empreendimento',
            value: filtroEmp,
            onChange: (v) => { setFiltroEmp(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              ...(empreendimentos.map((e) => ({ value: e.id, label: e.nome })))
            ]
          },
          {
            key: 'licenca',
            label: 'Licença',
            value: filtroLicenca,
            onChange: (v) => { setFiltroLicenca(v); setPage(1); },
            options: [
              { value: '', label: 'Todas' },
              ...(licencas.map((lic) => ({
                value: lic.id,
                label: `${lic.tipo || ''} ${lic.numero || ''}`.trim() || lic.empreendimento_nome || lic.id
              })))
            ]
          },
          {
            key: 'condicionante',
            label: 'Condicionante',
            value: filtroCond,
            onChange: (v) => { setFiltroCond(v); setPage(1); },
            options: [
              { value: '', label: 'Todas' },
              ...(condicionantes.slice(0, 80).map((c) => ({ value: c.id, label: (c.descricao || '').substring(0, 50) + (c.descricao && c.descricao.length > 50 ? '...' : '') })))
            ]
          },
          {
            key: 'responsavel',
            label: 'Enviado por',
            value: filtroResponsavel,
            onChange: (v) => { setFiltroResponsavel(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              ...(usuarios.map((u) => ({ value: u.id, label: u.nome })))
            ]
          },
          {
            key: 'vencimento_de',
            label: 'Prazo de',
            type: 'date',
            value: filtroVencimentoDe,
            onChange: (v) => { setFiltroVencimentoDe(v); setPage(1); }
          },
          {
            key: 'vencimento_ate',
            label: 'Prazo até',
            type: 'date',
            value: filtroVencimentoAte,
            onChange: (v) => { setFiltroVencimentoAte(v); setPage(1); }
          }
        ]}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        searchPlaceholder="Buscar por título ou tipo..."
      />

      {documentos.length === 0 ? (
        <div className="empty-state">
          <p>Nenhum documento encontrado.</p>
          <p className="form-hint">Documentos podem ser adicionados nos detalhes do empreendimento ou na validação de licenças.</p>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/empreendimentos')}>
            <FiFile /> Ir para empreendimentos
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Título</th>
                <th>Tipo</th>
                <th>Resumo</th>
                <th>Empreendimento</th>
                <th>Licença</th>
                <th>Tarefa</th>
                <th>Enviado por</th>
                <th>Prazo</th>
                <th>Data do documento</th>
                <th>Upload</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {documentos.map((doc) => (
                <tr key={doc.id}>
                  <td><strong className="documento-titulo-click" onClick={() => setDocSelecionado(doc)} title="Clique para ver detalhes">{doc.titulo}</strong></td>
                  <td>{doc.tipo || '-'}</td>
                  <td title={doc.descricao || ''}>{doc.descricao ? (doc.descricao.length > 60 ? `${doc.descricao.substring(0, 60)}...` : doc.descricao) : '-'}</td>
                  <td>
                    {doc.empreendimento_nome ? (
                      (doc.empreendimento_id_resolvido || doc.empreendimento_id) ? (
                        <Link to={`/empreendimentos/${doc.empreendimento_id_resolvido || doc.empreendimento_id}`} className="link-detalhes" style={{ textDecoration: 'none' }}>
                          {doc.empreendimento_nome}
                        </Link>
                      ) : doc.empreendimento_nome
                    ) : '-'}
                  </td>
                  <td>
                    {(doc.licenca_tipo || doc.licenca_numero) ? (
                      (doc.licenca_id_resolvido || doc.licenca_id) ? (
                        <Link to={`/licencas/${doc.licenca_id_resolvido || doc.licenca_id}/validar`} className="link-detalhes" style={{ textDecoration: 'none' }}>
                          {[doc.licenca_tipo, doc.licenca_numero].filter(Boolean).join(' ')}
                        </Link>
                      ) : ([doc.licenca_tipo, doc.licenca_numero].filter(Boolean).join(' ') || '-')
                    ) : '-'}
                  </td>
                  <td>
                    {doc.tarefa_id ? (
                      <Link to={`/tarefas/kanban?tarefa=${doc.tarefa_id}`} className="link-detalhes" style={{ textDecoration: 'none' }}>
                        {(doc.tarefa_titulo || '').length > 50 ? `${(doc.tarefa_titulo || '').substring(0, 50)}...` : (doc.tarefa_titulo || 'Tarefa')}
                      </Link>
                    ) : '-'}
                  </td>
                  <td>
                    {doc.usuario_upload_nome ? (
                      <div className="membro-avatar membro-avatar-sm" title={doc.usuario_upload_nome}>
                        {doc.usuario_upload_nome.charAt(0).toUpperCase()}
                      </div>
                    ) : (
                      <span className="membro-empty">-</span>
                    )}
                  </td>
                  <td>{formatDate(doc.prazo)}</td>
                  <td>{formatDate(doc.data_documento)}</td>
                  <td>{formatDate(doc.criado_em)}</td>
                  <td>
                    <div className="action-buttons">
                      <button type="button" className="btn btn-sm btn-secondary btn-icon" onClick={() => setDocSelecionado(doc)} title="Ver detalhes">
                        <FiEye />
                      </button>
                      {doc.arquivo && (
                        <a href={`${getDownloadBaseUrl()}${doc.arquivo}`} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary btn-icon" title="Baixar">
                          <FiDownload />
                        </a>
                      )}
                      <button type="button" className="btn btn-sm btn-secondary btn-icon" onClick={() => handleReprocess(doc)} title="Reprocessar documento" disabled={reprocessingId === doc.id}>
                        <FiRefreshCw />
                      </button>
                      <button type="button" className="btn btn-sm btn-secondary btn-icon" onClick={() => abrirEdicao(doc)} title="Editar dados do documento">
                        <FiEdit2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {docSelecionado && (
        <div className="modal-overlay" onClick={() => setDocSelecionado(null)}>
          <div className="modal-content modal-documento-detalhes" onClick={(e) => e.stopPropagation()}>
            <div className="modal-documento-header">
              <h3><FiFile className="modal-documento-icon" /> Detalhes do documento</h3>
              <button type="button" className="btn-close" onClick={() => setDocSelecionado(null)} aria-label="Fechar"><FiX /></button>
            </div>
            <div className="modal-documento-body">
              <div className="modal-documento-section modal-documento-titulo">
                <h4>{docSelecionado.titulo}</h4>
                {docSelecionado.tipo && <span className="badge badge-secondary">{docSelecionado.tipo}</span>}
              </div>
              {docSelecionado.descricao && (
                <div className="modal-documento-section">
                  <label>Resumo</label>
                  <p>{docSelecionado.descricao}</p>
                </div>
              )}
              <div className="modal-documento-grid">
                {docSelecionado.empreendimento_nome && (
                  <div className="modal-documento-item">
                    <label>Empreendimento</label>
                    <p>
                      {(docSelecionado.empreendimento_id_resolvido || docSelecionado.empreendimento_id) ? (
                        <Link to={`/empreendimentos/${docSelecionado.empreendimento_id_resolvido || docSelecionado.empreendimento_id}`} className="link-detalhes" onClick={(e) => e.stopPropagation()}>
                          {docSelecionado.empreendimento_nome}
                        </Link>
                      ) : docSelecionado.empreendimento_nome}
                    </p>
                  </div>
                )}
                {(docSelecionado.licenca_tipo || docSelecionado.licenca_numero) && (
                  <div className="modal-documento-item">
                    <label>Licença</label>
                    <p>
                      {(docSelecionado.licenca_id_resolvido || docSelecionado.licenca_id) ? (
                        <Link to={`/licencas/${docSelecionado.licenca_id_resolvido || docSelecionado.licenca_id}/validar`} className="link-detalhes" onClick={(e) => e.stopPropagation()}>
                          {[docSelecionado.licenca_tipo, docSelecionado.licenca_numero].filter(Boolean).join(' ')}
                        </Link>
                      ) : [docSelecionado.licenca_tipo, docSelecionado.licenca_numero].filter(Boolean).join(' ')}
                    </p>
                  </div>
                )}
                {docSelecionado.tarefa_id && (
                  <div className="modal-documento-item">
                    <label>Tarefa</label>
                    <p>
                      <Link to={`/tarefas/kanban?tarefa=${docSelecionado.tarefa_id}`} className="link-detalhes" onClick={(e) => e.stopPropagation()}>
                        {docSelecionado.tarefa_titulo || 'Ver tarefa'}
                      </Link>
                    </p>
                  </div>
                )}
                <div className="modal-documento-item">
                  <label>Enviado por</label>
                  <p>{docSelecionado.usuario_upload_nome || '-'}</p>
                </div>
                <div className="modal-documento-item">
                  <label>Data do documento</label>
                  <p>{formatDate(docSelecionado.data_documento)}</p>
                </div>
                <div className="modal-documento-item">
                  <label>Data de envio</label>
                  <p>{formatDate(docSelecionado.criado_em)}</p>
                </div>
                {docSelecionado.prazo && (
                  <div className="modal-documento-item">
                    <label>Prazo da tarefa</label>
                    <p>{formatDate(docSelecionado.prazo)}</p>
                  </div>
                )}
              </div>
              {docSelecionado.arquivo && (
                <>
                  <div className="modal-documento-section modal-documento-preview">
                    <label>Visualização do arquivo</label>
                    <DocumentoPreview
                      arquivo={docSelecionado.arquivo}
                      titulo={docSelecionado.titulo}
                      documentoId={docSelecionado.id}
                      textoExtraido={docSelecionado.texto_extraido}
                    />
                  </div>
                  <div className="modal-documento-actions">
                    <a href={getArquivoUrl(docSelecionado.arquivo)} target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                      <FiDownload /> Baixar arquivo
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {editando && (
        <div className="modal-overlay" onClick={() => setEditando(null)}>
          <div className="modal-content modal-documento-edicao" onClick={(e) => e.stopPropagation()}>
            <div className="modal-documento-header">
              <h3><FiEdit2 /> Editar dados do documento</h3>
              <button type="button" className="btn-close" onClick={() => setEditando(null)} aria-label="Fechar"><FiX /></button>
            </div>
            <form className="modal-documento-body" onSubmit={salvarEdicao}>
              <div className="form-group"><label>Tipo</label><select value={editForm.tipo} onChange={(e) => setEditForm({ ...editForm, tipo: e.target.value })}>{TIPOS_DOCUMENTO.map((tipo) => <option key={tipo} value={tipo}>{tipo}</option>)}</select></div>
              <div className="form-group"><label>Resumo</label><textarea rows="4" value={editForm.descricao} onChange={(e) => setEditForm({ ...editForm, descricao: e.target.value })} /></div>
              <div className="form-group"><label>Empreendimento</label><select value={editForm.empreendimento_id} onChange={(e) => setEditForm({ ...editForm, empreendimento_id: e.target.value, licenca_id: '', tarefa_id: '' })}><option value="">Sem empreendimento</option>{empreendimentos.map((emp) => <option key={emp.id} value={emp.id}>{emp.nome}</option>)}</select></div>
              <div className="form-group"><label>Licença</label><select value={editForm.licenca_id} onChange={(e) => setEditForm({ ...editForm, licenca_id: e.target.value, tarefa_id: '' })}><option value="">Sem licença</option>{licencas.filter((lic) => !editForm.empreendimento_id || lic.empreendimento_id === editForm.empreendimento_id).map((lic) => <option key={lic.id} value={lic.id}>{[lic.tipo, lic.numero].filter(Boolean).join(' ') || lic.id}</option>)}</select></div>
              <div className="form-group"><label>Tarefa</label><select value={editForm.tarefa_id} onChange={(e) => setEditForm({ ...editForm, tarefa_id: e.target.value })}><option value="">Sem tarefa</option>{tarefas.filter((tarefa) => !editForm.empreendimento_id || tarefa.empreendimento_id === editForm.empreendimento_id).map((tarefa) => <option key={tarefa.id} value={tarefa.id}>{tarefa.titulo}</option>)}</select></div>
              <div className="form-row">
                <div className="form-group"><label>Data do documento</label><input type="date" value={editForm.data_documento} onChange={(e) => setEditForm({ ...editForm, data_documento: e.target.value })} /></div>
                <div className="form-group"><label>Data de upload</label><input type="text" value={new Date(editando.criado_em).toLocaleString('pt-BR')} readOnly /></div>
              </div>
              <div className="form-actions"><button type="button" className="btn btn-secondary" onClick={() => setEditando(null)}>Cancelar</button><button type="submit" className="btn btn-primary">Salvar alterações</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documentos;
